<template>
    <transition name="benefits-modal-fade">
        <div class="benefits-modal" v-if="isOpen" @click.self="close">
            <div
                class="benefits-modal-panel"
                ref="panel"
                role="dialog"
                aria-modal="true"
                :style="panelStyle"
            >
                <slot name="header">
                    <place-header show-back class="benefits-modal-header" @back="close" />
                </slot>

                <div class="benefits-modal-content">
                    <slot></slot>
                </div>

                <div class="benefits-modal-footer" v-if="$slots.footer">
                    <slot name="footer"></slot>
                </div>

                <div class="benefits-modal-footer is-fallback" v-else>
                    <button type="button" class="benefits-modal-close" @click="close">
                        <span v-lang="'dashboard-modalClose'"></span>
                    </button>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import PlaceHeader from "./place-header";

export default {
    name: "BenefitsModal",
    components: { PlaceHeader },
    props: {
        name: {
            type: String,
            default: "",
        },
        openWhen: {
            default: null,
        },
        background: {
            type: String,
            default: "#ffffff",
        },
    },
    computed: {
        ...mapState({
            place: (state) => state.place,
            modal: (state) => state.modal,
        }),
        isOpen() {
            return this.openWhen === null ? this.modal === this.name : !!this.openWhen;
        },
        panelStyle() {
            const config = this.place.benefit_program || {};

            return {
                backgroundColor: this.background,
            };
        },
    },
    watch: {
        isOpen(open) {
            this.lockScroll();
            this.$emit(open ? "open" : "close");
        },
    },
    mounted() {
        document.addEventListener("keydown", this.onKeydown);
    },
    beforeUnmounted() {
        document.removeEventListener("keydown", this.onKeydown);
        this.setScrollLock(false);
    },
    methods: {
        ...mapMutations(["closeModal"]),
        close() {
            if (this.openWhen !== null) {
                this.$emit("request-close");
                return;
            }

            this.closeModal();
        },
        scrollToTop() {
            if (this.$refs.panel) {
                this.$refs.panel.scrollTop = 0;
            }
        },
        scrollElement() {
            return this.$refs.panel || null;
        },
        onKeydown(event) {
            if (this.isOpen && (event.key === "Escape" || event.keyCode === 27)) {
                this.close();
            }
        },
        lockScroll() {
            const state = this.$store.state;

            this.setScrollLock(!!(state.modal || state.rewardId || state.redeemed));
        },
        setScrollLock(locked) {
            const value = locked ? "hidden" : "";

            document.documentElement.style.overflow = value;
            document.body.style.overflow = value;
        },
    },
};
</script>

<style lang="scss" scoped>
.benefits-modal {
  bottom: 0;
  display: flex;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 100;
}

.benefits-modal-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  position: relative;
  width: 100%;
}

.benefits-modal-content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
}

.benefits-modal-footer {
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  bottom: 0;
  display: flex;
  flex: 0 0 auto;
  justify-content: flex-end;
  padding: 1rem;
  position: sticky;
  z-index: 10;

  &.is-fallback {
    display: none;
  }
}

@media (min-width: 48rem) {
  .benefits-modal {
    align-items: center;
    background: rgba(13, 18, 28, 0.55);
    justify-content: center;
    padding: 2rem 1rem;
  }

  .benefits-modal-panel {
    border-radius: 1.5rem;
    box-shadow: 0 1.5rem 3rem rgba(0, 0, 0, 0.25);
    height: 50.75rem;
    max-height: 100%;
    max-width: 56.25rem;
    width: 100%;
  }

  .benefits-modal-header ::v-deep .place-header-back,
  .benefits-modal-header ::v-deep .place-header-menu {
    display: none;
  }

  .benefits-modal-footer.is-fallback {
    display: flex;
  }

  .benefits-modal-close {
    align-items: center;
    background: #fcfcfd;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    color: #1f2a37;
    cursor: pointer;
    display: inline-flex;
    font-size: 1rem;
    justify-content: center;
    line-height: 1.2;
    min-height: 2.6875rem;
    padding: 0.75rem;
    transition: transform 0.1s;

    &:active {
      transform: scale(0.98);
    }
  }
}

.benefits-modal-fade-enter-active,
.benefits-modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.benefits-modal-fade-enter,
.benefits-modal-fade-leave-to {
  opacity: 0;
}
</style>
